import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, X, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AgencyProfiles() {
  const navigate = useNavigate();
  const [agencyProfiles, setAgencyProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all'); // all | Check | ACH
  const [statusFilter, setStatusFilter] = useState('Active'); // Active | Prospective | Inactive | all
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAgency, setNewAgency] = useState({
    agency_name: '',
    state: '',
    address: '',
    city: '',
    zip_code: '',
    payment_type: 'Check',
    status: 'Active',
  });

  useEffect(() => {
    fetchAgencyProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Refetch when tab/window regains focus (catches new agencies added from other pages)
  useEffect(() => {
    const handleFocus = () => fetchAgencyProfiles();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const promoteAgencyToActive = async (e, agencyId, name) => {
    e.stopPropagation();
    if (!window.confirm(`Promote "${name}" from Prospective to Active? It will move to your active agencies list.`)) return;
    try {
      await axios.put(`${API}/agencies/${agencyId}/status`, { status: 'Active' });
      toast.success(`${name} is now Active`);
      fetchAgencyProfiles();
    } catch (_) {
      toast.error('Failed to promote agency');
    }
  };

  const fetchAgencyProfiles = async () => {
    try {
      const response = await axios.get(`${API}/agency-profiles`, { params: { status: statusFilter === 'all' ? 'all' : statusFilter } });
      // Sort alphabetically by agency name
      const sorted = response.data.sort((a, b) => 
        (a.agency_name || '').localeCompare(b.agency_name || '')
      );
      setAgencyProfiles(sorted);
    } catch (error) {
      toast.error('Failed to load agency profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAgency = async (e) => {
    e.preventDefault();
    if (!newAgency.agency_name) {
      toast.error('Agency name is required');
      return;
    }
    try {
      await axios.post(`${API}/agency-profiles`, newAgency);
      toast.success('Agency added successfully');
      setShowAddModal(false);
      setNewAgency({ agency_name: '', state: '', address: '', city: '', zip_code: '', payment_type: 'Check', status: 'Active' });
      fetchAgencyProfiles();
    } catch (error) {
      toast.error('Failed to add agency');
    }
  };

  const handleDeleteAgency = async (e, agencyId, name) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${name}? This will not delete associated visits.`)) return;
    try {
      await axios.delete(`${API}/agency-profiles/${agencyId}`);
      toast.success('Agency deleted successfully');
      fetchAgencyProfiles();
    } catch (error) {
      toast.error('Failed to delete agency');
    }
  };

  const filteredAgencies = (search 
    ? agencyProfiles.filter(a => a.agency_name.toLowerCase().includes(search.toLowerCase()))
    : agencyProfiles
  )
    .filter(a => showInactive ? true : a.is_active !== false)
    .filter(a => paymentTypeFilter === 'all' ? true : (a.payment_type || 'Check') === paymentTypeFilter);

  const checkCount = agencyProfiles.filter(a => (a.payment_type || 'Check') === 'Check' && a.is_active !== false).length;
  const achCount = agencyProfiles.filter(a => (a.payment_type || 'Check') === 'ACH' && a.is_active !== false).length;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            Agencies
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Manage and view all agency profiles
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="gap-2" data-testid="add-agency-btn">
          <Plus size={18} />
          Add Agency
        </Button>
      </div>

      <Card className="p-6">
        {/* Status tabs */}
        <div className="mb-4 flex items-center gap-2 border-b pb-3" data-testid="agency-status-tabs">
          {[
            { value: 'Active', label: 'Active' },
            { value: 'Prospective', label: 'Prospective (Marketing)' },
            { value: 'Inactive', label: 'Inactive' },
            { value: 'all', label: 'All' },
          ].map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setStatusFilter(t.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === t.value ? 'bg-primary text-white' : 'bg-background hover:bg-muted'
              }`}
              data-testid={`agency-status-tab-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              type="text"
              placeholder="Search agencies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-agencies"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="show-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4"
            />
            Show inactive ({agencyProfiles.filter(a => a.is_active === false).length})
          </label>

          <div className="flex items-center gap-2 text-sm" data-testid="payment-type-filter">
            <span className="text-muted-foreground">Payment:</span>
            {[
              { value: 'all',   label: `All (${checkCount + achCount})` },
              { value: 'Check', label: `Check (${checkCount})` },
              { value: 'ACH',   label: `ACH (${achCount})` },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPaymentTypeFilter(opt.value)}
                className={`px-3 py-1 rounded-md border text-xs font-medium transition-colors ${
                  paymentTypeFilter === opt.value
                    ? 'bg-primary text-white border-primary'
                    : 'bg-background border-input hover:bg-muted'
                }`}
                data-testid={`payment-filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading agencies...</div>
        ) : filteredAgencies.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="mx-auto mb-4 text-muted-foreground" size={48} strokeWidth={1.5} />
            <p className="text-muted-foreground">No agencies found</p>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Agency Name
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    State
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Payment
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Total Visits
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAgencies.map((profile) => (
                  <tr
                    key={profile.id}
                    className="border-b border-border hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/agencies/${profile.id}`)}
                    data-testid="agency-row"
                  >
                    <td className="p-4 text-sm text-foreground font-medium">
                      <div className="flex items-center gap-2">
                        {profile.agency_name}
                        {profile.is_active === false && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Inactive</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-foreground">
                      {profile.state || '-'}
                    </td>
                    <td className="p-4 text-sm">
                      {(() => {
                        const pt = profile.payment_type || 'Check';
                        const cls = pt === 'ACH'
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'bg-emerald-100 text-emerald-700 border-emerald-200';
                        return (
                          <span className={`text-xs px-2 py-0.5 rounded-md border font-medium ${cls}`} data-testid={`agency-payment-${profile.id}`}>
                            {pt}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-sm text-foreground tabular-nums">
                      {profile.total_visits}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        {(profile.status === 'Prospective') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => promoteAgencyToActive(e, profile.id, profile.agency_name)}
                            data-testid={`promote-agency-${profile.id}`}
                            className="gap-1 text-xs"
                          >
                            Promote → Active
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => handleDeleteAgency(e, profile.id, profile.agency_name)}
                          data-testid={`delete-agency-${profile.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Agency Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Add New Agency</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={handleAddAgency} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Agency Name *</label>
                <Input
                  value={newAgency.agency_name}
                  onChange={(e) => setNewAgency({ ...newAgency, agency_name: e.target.value })}
                  placeholder="Agency name"
                  data-testid="new-agency-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Street Address</label>
                <Input
                  value={newAgency.address}
                  onChange={(e) => setNewAgency({ ...newAgency, address: e.target.value })}
                  placeholder="123 Main St"
                  data-testid="new-agency-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">City</label>
                  <Input
                    value={newAgency.city}
                    onChange={(e) => setNewAgency({ ...newAgency, city: e.target.value })}
                    placeholder="Dallas"
                    data-testid="new-agency-city"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">State</label>
                  <Input
                    value={newAgency.state}
                    onChange={(e) => setNewAgency({ ...newAgency, state: e.target.value })}
                    placeholder="TX"
                    data-testid="new-agency-state"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">ZIP (for map)</label>
                  <Input
                    value={newAgency.zip_code}
                    onChange={(e) => setNewAgency({ ...newAgency, zip_code: e.target.value })}
                    placeholder="75204"
                    data-testid="new-agency-zip"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Payment Type *</label>
                <div className="flex items-center gap-3 mt-1.5" data-testid="new-agency-payment-type">
                  {['Check', 'ACH'].map(opt => {
                    const active = newAgency.payment_type === opt;
                    return (
                      <label
                        key={opt}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${
                          active ? 'bg-primary/10 border-primary text-primary font-medium' : 'bg-background border-input hover:bg-muted'
                        }`}
                      >
                        <input
                          type="radio"
                          name="new-agency-payment-type"
                          checked={active}
                          onChange={() => setNewAgency({ ...newAgency, payment_type: opt })}
                          className="h-3.5 w-3.5"
                          data-testid={`new-agency-payment-${opt.toLowerCase()}`}
                        />
                        {opt}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <div className="flex items-center gap-3 mt-1.5" data-testid="new-agency-status-options">
                  {['Active', 'Prospective'].map(opt => {
                    const active = newAgency.status === opt;
                    return (
                      <label key={opt} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors ${active ? 'bg-primary/10 border-primary text-primary font-medium' : 'bg-background border-input hover:bg-muted'}`}>
                        <input type="radio" name="new-agency-status" checked={active} onChange={() => setNewAgency({ ...newAgency, status: opt })} className="h-3.5 w-3.5" data-testid={`new-agency-status-${opt.toLowerCase()}`} />
                        {opt}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Use "Prospective" for marketing leads. They won't show on the main Active tab.</p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-new-agency">
                  Add Agency
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
